/**
 * Robust JSON Parser with Error Recovery
 * Handles malformed JSON from AI responses with multiple recovery strategies
 */

export interface ParseResult<T = any> {
  success: boolean;
  data: T | null;
  error: string | null;
  strategy: 'direct' | 'clean' | 'extract' | 'repair' | null;
}

/**
 * Parse JSON with comprehensive error recovery
 */
export function parseJSON<T = any>(
  content: string,
  contentType: string = 'JSON'
): ParseResult<T> {
  if (!content || typeof content !== 'string') {
    return {
      success: false,
      data: null,
      error: `Invalid input: expected string, got ${typeof content}`,
      strategy: null
    };
  }

  // Trim and validate minimum length
  const trimmed = content.trim();
  if (trimmed.length === 0) {
    return {
      success: false,
      data: null,
      error: 'Content is empty',
      strategy: null
    };
  }

  // Check if content looks like JSON
  const firstChar = trimmed[0];
  const lastChar = trimmed[trimmed.length - 1];
  if (!(firstChar === '{' || firstChar === '[') || !(lastChar === '}' || lastChar === ']')) {
    console.warn(`⚠️  Content doesn't appear to be JSON. First char: "${firstChar}", Last char: "${lastChar}"`);
  }

  // Strategy 1: Direct parse
  try {
    const data = JSON.parse(trimmed);
    console.log(`✅ ${contentType} parsed successfully (direct)`);
    return { success: true, data, error: null, strategy: 'direct' };
  } catch (e) {
    console.warn(`⚠️  Direct ${contentType} parse failed, attempting recovery...`);
  }

  // Clean and preprocess
  let cleaned = trimmed
    .replace(/\x1b\[[0-9;]*m/g, '') // Remove ANSI escape sequences
    .replace(/\[\d{2}m/g, '') // Remove bracket format codes
    .replace(/\[0m/g, '') // Remove reset codes
    .trim();

  // Strategy 2: Remove markdown code blocks
  const withoutMarkdown = cleaned
    .replace(/^```(?:json)?[\s\n]*/i, '')
    .replace(/[\s\n]*```$/i, '')
    .trim();

  if (withoutMarkdown !== cleaned) {
    try {
      const data = JSON.parse(withoutMarkdown);
      console.log(`✅ ${contentType} parsed successfully (markdown cleanup)`);
      return { success: true, data, error: null, strategy: 'clean' };
    } catch (e) {
      console.warn(`⚠️  Markdown cleanup parse failed, continuing...`);
    }
    cleaned = withoutMarkdown;
  }

  // Strategy 3: Extract JSON object/array
  const jsonMatch = cleaned.match(/[\[{][\s\S]*[\]}]/);
  if (jsonMatch) {
    try {
      const data = JSON.parse(jsonMatch[0]);
      console.log(`✅ ${contentType} parsed successfully (extraction)`);
      return { success: true, data, error: null, strategy: 'extract' };
    } catch (e) {
      console.warn(`⚠️  JSON extraction parse failed, attempting repair...`);
    }
  }

  // Strategy 4: Repair common JSON issues
  let repaired = cleaned;

  try {
    // Fix trailing commas
    repaired = repaired
      .replace(/,\s*([}\]])/g, '$1')
      .replace(/([}\]])\s*,\s*([}\]])/g, '$1$2');

    // Fix unescaped characters in strings
    // This is more sophisticated - we need to find strings and fix them
    repaired = fixUnescapedCharacters(repaired);

    // Fix unclosed strings by looking for orphaned quotes
    repaired = fixUnclosedStrings(repaired);

    // Balance brackets
    repaired = balanceBrackets(repaired);

    const data = JSON.parse(repaired);
    console.log(`✅ ${contentType} parsed successfully (repair)`);
    return { success: true, data, error: null, strategy: 'repair' };
  } catch (e) {
    const errorMsg = e instanceof Error ? e.message : String(e);
    console.error(`❌ All ${contentType} parse strategies failed:`, errorMsg);
    console.log(`📋 Last attempt (first 500 chars):`, repaired.substring(0, 500));
    
    return {
      success: false,
      data: null,
      error: errorMsg,
      strategy: null
    };
  }
}

/**
 * Fix unescaped special characters within JSON strings
 */
function fixUnescapedCharacters(json: string): string {
  let result = '';
  let inString = false;
  let escapeNext = false;

  for (let i = 0; i < json.length; i++) {
    const char = json[i];
    const nextChar = json[i + 1];

    if (escapeNext) {
      result += char;
      escapeNext = false;
      continue;
    }

    if (char === '\\') {
      escapeNext = true;
      result += char;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      result += char;
      continue;
    }

    if (inString) {
      // Inside a string - need to escape special characters
      if (char === '\n') {
        result += '\\n';
      } else if (char === '\r') {
        result += '\\r';
      } else if (char === '\t') {
        result += '\\t';
      } else if (char === '"' && nextChar !== ':') {
        result += '\\"';
      } else {
        result += char;
      }
    } else {
      result += char;
    }
  }

  return result;
}

/**
 * Fix unclosed strings at the end of JSON
 */
function fixUnclosedStrings(json: string): string {
  let quoteCount = 0;
  let inString = false;
  let escapeNext = false;

  // Count unescaped quotes
  for (let i = 0; i < json.length; i++) {
    const char = json[i];

    if (escapeNext) {
      escapeNext = false;
      continue;
    }

    if (char === '\\') {
      escapeNext = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      quoteCount++;
    }
  }

  // If odd number of quotes, close the string
  if (quoteCount % 2 === 1) {
    return json + '"';
  }

  return json;
}

/**
 * Balance brackets in JSON
 */
function balanceBrackets(json: string): string {
  let braceCount = 0;
  let bracketCount = 0;
  let inString = false;
  let escapeNext = false;

  for (let i = 0; i < json.length; i++) {
    const char = json[i];

    if (escapeNext) {
      escapeNext = false;
      continue;
    }

    if (char === '\\') {
      escapeNext = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (!inString) {
      if (char === '{') braceCount++;
      if (char === '}') braceCount--;
      if (char === '[') bracketCount++;
      if (char === ']') bracketCount--;
    }
  }

  // Close open braces
  while (braceCount > 0) {
    json += '}';
    braceCount--;
  }

  // Close open brackets
  while (bracketCount > 0) {
    json += ']';
    bracketCount--;
  }

  return json;
}

/**
 * Parse JSON array with per-item error recovery
 */
export function parseJSONArray<T = any>(
  content: string,
  contentType: string = 'JSON Array'
): ParseResult<T[]> {
  const result = parseJSON<T[]>(content, contentType);
  
  if (!result.success) {
    return result;
  }

  if (!Array.isArray(result.data)) {
    return {
      success: false,
      data: null,
      error: 'Parsed content is not an array',
      strategy: result.strategy
    };
  }

  return result;
}

/**
 * Parse JSON object with validation
 */
export function parseJSONObject<T = any>(
  content: string,
  contentType: string = 'JSON Object',
  requiredFields?: string[]
): ParseResult<T> {
  const result = parseJSON<T>(content, contentType);

  if (!result.success) {
    return result;
  }

  if (typeof result.data !== 'object' || result.data === null || Array.isArray(result.data)) {
    return {
      success: false,
      data: null,
      error: 'Parsed content is not an object',
      strategy: result.strategy
    };
  }

  // Validate required fields
  if (requiredFields) {
    const obj = result.data as Record<string, any>;
    const missing = requiredFields.filter(field => !(field in obj));
    
    if (missing.length > 0) {
      return {
        success: false,
        data: null,
        error: `Missing required fields: ${missing.join(', ')}`,
        strategy: result.strategy
      };
    }
  }

  return result;
}
